import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import styles from '../../../styles/layout.module.css';
import tileStyles from '../../../styles/tile.module.css';
import { Breadcrumb, Button, Divider, Input, Row, Select, Space, Spin, Typography } from 'antd';
import { Link, useHistory, useParams } from 'react-router-dom';

import EditableField from '../../fields/EditableField';
import { CloseOutlined, DeleteOutlined, EditOutlined, ProjectTwoTone, ShopTwoTone } from '@ant-design/icons';
import { AsyncEntity } from 'src/global';
import format from 'date-fns/format';
import { useFirmwareById } from 'src/features/firmwares/firmwares.hooks';
import { Firmware } from 'src/features/firmwares/firmwares.types';
import { useCompaniesValues } from 'src/features/companies/companies.hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import ConfirmButton from 'src/components/atoms/ConfirmButton';
import { useDispatch } from 'react-redux';
import { addFirmwareCompany, removeFirmwareCompany } from 'src/features/firmwares/firmwares.state';
import { fetchCompanies } from 'src/features/companies/companies.state';
import LoaderWrapper from 'src/components/atoms/LoaderWrapper';

const { Option } = Select;

const FirmwarePage: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { firmwareId } = useParams() as {
    firmwareId: string;
  };
  const [firmware, { updateFirmware, deleteFirmware }] = useFirmwareById({
    id: Number(firmwareId),
  });
  const [createMode, setCreateMode] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const intl = useIntl();

  const companies = useCompaniesValues();
  const firmwareCompanies = useMemo(() => firmware?.companies || [], [firmware]);
  const isCompanyInFirmware = useCallback(
    (id: string) => firmwareCompanies.map(c => c.id).includes(id),
    [firmware, firmwareCompanies]
  );

  const currentCompanies = useMemo(() => {
    const accs = createMode ? companies : firmwareCompanies;
    return searchValue.length > 0
      ? accs.filter(a => a.name.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()))
      : accs;
  }, [companies, firmwareCompanies, createMode, searchValue]);

  const companyActions = useCallback(
    (companyId: string) => {
      const isInCompany = isCompanyInFirmware(companyId);
      if (isInCompany) {
        return (
          <ConfirmButton
            danger
            size='small'
            onClick={() => dispatch(removeFirmwareCompany({ id: Number(firmwareId), companyId }))}
          >
            {isInCompany ? 'Удалить' : 'Добавить'}
          </ConfirmButton>
        );
      }
      return (
        <Button
          size='small'
          onClick={() => dispatch(addFirmwareCompany({ id: Number(firmwareId), data: { companyId } }))}
        >
          {isInCompany ? 'Удалить' : 'Добавить'}
        </Button>
      );
    },
    [firmwareId, dispatch, isCompanyInFirmware]
  );

  const [draftFirmware, setDraftFirmware] = useState<Partial<AsyncEntity<Firmware>>>(firmware as Firmware);

  const handleDeleteButtonClick = useCallback(async () => {
    await deleteFirmware();
    history.push('/settings/firmwares');
  }, [deleteFirmware, history]);

  const handleCompanyUpdate = useCallback(() => {
    updateFirmware({ firmware: draftFirmware });
  }, [draftFirmware, updateFirmware]);

  const parseDate = useCallback((date: string) => {
    if (!date) {
      return '';
    }
    return format(new Date(date), 'd MMMM y hh:mm');
  }, []);

  useEffect(() => {
    handleCompanyUpdate();
  }, [draftFirmware, handleCompanyUpdate]);

  const [companiesLoading, setCompaniesLoading] = useState(false);
  useEffect(() => {
    setCompaniesLoading(true);
    (async () => {
      await dispatch(fetchCompanies({ name: '' }));
      setCompaniesLoading(false);
    })();
  }, [setCompaniesLoading, dispatch]);

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/settings/firmwares'>
                <Space>
                  <ProjectTwoTone />
                  <FormattedMessage id={'page.firmware.breadcrumb.firmwares'} />
                </Space>
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {firmware?.name}
              {firmware?.ng && <span> (New Generation)</span>}
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Typography.Title level={2} className={styles.title}>
            {firmware?.name} {firmware?.ng && <span> (New Generation)</span>}
          </Typography.Title>
          <Space size='small' />
          <div className={styles.root}>
            <div className={styles.header}>
              <Typography.Title level={4}>
                <FormattedMessage id={'page.firmware.section.companies.title'} />
              </Typography.Title>
              <Button onClick={() => setCreateMode(m => !m)}>
                {createMode ? <CloseOutlined /> : <EditOutlined />}
              </Button>
            </div>
            <Input
              onChange={e => setSearchValue(e.target.value)}
              allowClear
              autoFocus
              placeholder={intl.formatMessage({ id: 'page.firmware.section.companies.input.search.placeholder' })}
            />
            <LoaderWrapper loading={companiesLoading}>
              <div className={styles.content}>
                {currentCompanies.map(company => (
                  <div key={company.id} className={tileStyles.listTile}>
                    <Link to={`/commercial/company/${company.id}`} className={tileStyles.tileName}>
                      <Space>
                        <ShopTwoTone />
                        <span>{company.name}</span>
                      </Space>
                    </Link>
                    <Space>{createMode && companyActions(company.id)}</Space>
                  </div>
                ))}
              </div>
            </LoaderWrapper>
          </div>
        </div>
        <div className={styles.sidebar}>
          <div className={styles.propertiesGrid}>
            <Typography.Text className={styles.title}>
              <FormattedMessage id={'page.firmware.field.file'} />
            </Typography.Text>
            <a href={firmware?.url} target='_blank' rel='noreferrer noopener'>
              <FormattedMessage id={'action.open'} />
            </a>
            <Typography.Text className={styles.title}>
              <FormattedMessage id={'page.firmware.field.isTest'} />
            </Typography.Text>
            <EditableField
              value={
                firmware?.isTest ? intl.formatMessage({ id: 'action.yes' }) : intl.formatMessage({ id: 'action.no' })
              }
            >
              <Select
                value={
                  draftFirmware.isTest
                    ? intl.formatMessage({ id: 'action.yes' })
                    : intl.formatMessage({ id: 'action.no' })
                }
                onSelect={e => setDraftFirmware({ isTest: Number(e) === 1 })}
                style={{ width: '100%' }}
                defaultOpen
              >
                {[
                  { label: intl.formatMessage({ id: 'action.yes' }), value: 1 },
                  { label: intl.formatMessage({ id: 'action.no' }), value: 0 },
                ].map(l => (
                  <Option key={l.label} value={l.value}>
                    {l.label}
                  </Option>
                ))}
              </Select>
            </EditableField>
            <Typography.Text className={styles.title}>
              <FormattedMessage id={'page.firmware.field.isActive'} />
            </Typography.Text>
            <EditableField
              value={
                firmware?.isActive ? intl.formatMessage({ id: 'action.yes' }) : intl.formatMessage({ id: 'action.no' })
              }
            >
              <Select
                value={
                  draftFirmware.isActive
                    ? intl.formatMessage({ id: 'action.yes' })
                    : intl.formatMessage({ id: 'action.no' })
                }
                onSelect={e => setDraftFirmware({ isActive: Number(e) === 1 })}
                style={{ width: '100%' }}
                defaultOpen
              >
                {[
                  { label: intl.formatMessage({ id: 'action.yes' }), value: 1 },
                  { label: intl.formatMessage({ id: 'action.no' }), value: 0 },
                ].map(l => (
                  <Option key={l.label} value={l.value}>
                    {l.label}
                  </Option>
                ))}
              </Select>
            </EditableField>
            <Typography.Text className={styles.title}>
              <FormattedMessage id={'page.firmware.field.isPrivate'} />
            </Typography.Text>
            <EditableField
              value={
                firmware?.isPrivate ? intl.formatMessage({ id: 'action.yes' }) : intl.formatMessage({ id: 'action.no' })
              }
            >
              <Select
                value={
                  draftFirmware.isPrivate
                    ? intl.formatMessage({ id: 'action.yes' })
                    : intl.formatMessage({ id: 'action.no' })
                }
                onSelect={e => setDraftFirmware({ isPrivate: Number(e) === 1 })}
                style={{ width: '100%' }}
                defaultOpen
              >
                {[
                  { label: intl.formatMessage({ id: 'action.yes' }), value: 1 },
                  { label: intl.formatMessage({ id: 'action.no' }), value: 0 },
                ].map(l => (
                  <Option key={l.label} value={l.value}>
                    {l.label}
                  </Option>
                ))}
              </Select>
            </EditableField>
          </div>
          <Divider />
          <Space direction='vertical' size='small'>
            <Typography.Text className={styles.footerDateStamp}>md5:&nbsp;&nbsp;{firmware?.md5}</Typography.Text>
            <Typography.Text className={styles.footerDateStamp}>
              <FormattedMessage id={'field.createdAt'} values={{ date: parseDate(firmware?.createdAt || '') }} />
            </Typography.Text>
            <Typography.Text className={styles.footerDateStamp}>
              <FormattedMessage id={'field.uploadedAt'} values={{ date: parseDate(firmware?.uploadedAt || '') }} />
            </Typography.Text>
            <Button type='link' onClick={handleDeleteButtonClick} danger size='small' style={{ padding: 0 }}>
              <DeleteOutlined /> <FormattedMessage id={'page.firmware.action.deleteFirmware'} />
            </Button>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const FirmwarePageBlock: React.FC = () => {
  const { firmwareId } = useParams() as {
    firmwareId: string;
  };
  const [firmware, { fetchFirmware }] = useFirmwareById({
    id: Number(firmwareId),
  });

  useEffect(() => {
    fetchFirmware();
  }, [fetchFirmware]);

  if (!firmware) {
    return (
      <Row align={'middle'} justify={'center'} style={{ minHeight: '100vh' }}>
        <Spin size={'large'} />
      </Row>
    );
  }

  return <FirmwarePage />;
};

export default FirmwarePageBlock;
